import * as THREE from 'three';
import type { MoveInput } from '../core/input';
import type { BlockPhysics } from './collision';

export const PLAYER_RADIUS = 0.3;
export const PLAYER_TOTAL_HEIGHT = 1.4; // ASSET-CONTRACT.md §1: Runner is 1.4m (chibi) — the world-scale anchor

// GRAVITY/JUMP_SPEED/MOVE_SPEED are exported so layout.ts can DERIVE collider
// heights AND horizontal jump-gap distances from the same numbers, instead of
// hand-copying a derived value and silently drifting the day a tuning session
// touches any of the three (T1 review finding, extended by B-1's rooftop
// jump-gap work — see layout.ts's ROOFTOP_B jump-gap comment).
export const MOVE_SPEED = 4.8; // m/s — placeholder feel value, T1-T3 tuning owns the final number
export const GRAVITY = -20;
export const JUMP_SPEED = 7.5; // v0 for GRAVITY=-20 clears ~1.4m, a full body-height hop
const COYOTE_TIME = 0.12;
const JUMP_BUFFER_TIME = 0.12;
// Ground-region selection tolerance for `groundHeightAt`/`findVaultLedge`'s
// floating-ledge check. Deliberately smaller than any vaultable prop height
// in this block (0.45-0.5m) — see layout.ts's PARK_PROP_TOPS comment: at
// 0.5 this sat exactly on the boundary, ambiguous with a 0.5m-tall prop's
// own floor region and would pull the player up onto it while merely
// walking past at street level, before any vault happened.
const STEP_TOLERANCE = 0.2;
// T1 3c: how far below `prevY` the ground can sit and still count as
// "still walking the slope" rather than "started a real fall" — see the
// stick-to-ground comment in `update()`. Deliberately larger than
// STEP_TOLERANCE above (that one gates which floor REGION wins a tie, not
// how far a single tick may snap down) and comfortably bigger than one
// tick's worth of a 45°-ramp descent at MOVE_SPEED, but far smaller than
// any real drop in this block (the shallowest is the ~3.5m subway landing).
const STEP_DOWN_TOLERANCE = 0.45;
// Exported so layout.ts can prove a jump-only ledge (B-1's rooftop vantage
// tower) is genuinely ABOVE auto-vault range, not just "looks tall enough" —
// same derive-don't-hand-copy discipline as MOVE_SPEED/GRAVITY/JUMP_SPEED above.
export const VAULT_MAX_HEIGHT = 1.1;
const VAULT_PROBE_DIST = PLAYER_RADIUS + 0.4;
const VAULT_DURATION = 0.28;
const SLIDE_SPEED = MOVE_SPEED * 1.85;
const SLIDE_DURATION = 0.35;
const TURN_RATE = 14; // rad/s, mesh yaw catch-up to movement direction
const WHITE = new THREE.Color(0xffffff);

function shortestAngleLerp(from: number, to: number, t: number): number {
  let diff = ((to - from + Math.PI) % (Math.PI * 2)) - Math.PI;
  if (diff < -Math.PI) diff += Math.PI * 2;
  return from + diff * t;
}

export interface ControllerState {
  grounded: boolean;
  sliding: boolean;
  vaulting: boolean;
  invulnerable: boolean;
}

/**
 * The real S01 kinematic capsule controller: 8-dir camera-relative move,
 * jump (coyote + input buffer), context-auto vault over low ledges, and a
 * slide/dodge that grants i-frames — all resolved against `BlockPhysics`
 * instead of S00's flat-plane placeholder. Public shape (mesh, position,
 * addTo, teleport, update) is the same seam S00 left; `update`'s signature
 * grew to take camera-relative axes + action requests, which every call
 * site (main.ts) already expected per S00's own header comment.
 */
export class PlayerCapsule {
  readonly mesh: THREE.Mesh;
  readonly position = new THREE.Vector3(0, 0, 4);
  readonly state: ControllerState = { grounded: true, sliding: false, vaulting: false, invulnerable: false };

  private velocityY = 0;
  private coyoteTimer = 0;
  private jumpBufferTimer = 0;
  private facing = 0; // yaw, radians

  private vaultTimer = 0;
  private vaultFrom = new THREE.Vector3();
  private vaultTo = new THREE.Vector3();

  private slideTimer = 0;
  private slideDirX = 0;
  private slideDirZ = 0;

  /** R-4 (PLAN-CITY3 §2): the true commanded speed (m/s) this tick — `world/playerVisual.ts` reads this via `getCommandedSpeed()` to couple the run clip's timeScale to actual movement (a partial-stick analog push, not just the flat MOVE_SPEED constant) instead of always animating at full authored speed. */
  private commandedSpeed = 0;

  /** The desired (input-intent) world-space move direction from the most recent update() tick — see the capture site's comment. Readable, never written from outside. */
  lastDesiredDirX = 0;
  lastDesiredDirZ = 0;

  private readonly shadowBlob: THREE.Mesh;
  // Same lerp-from-base-emissive flash technique as ai/enemy.ts's
  // Enemy.applyFlashTint — kept independently here since PlayerCapsule
  // doesn't share Enemy's base class. Captured once so a future material
  // change (e.g. a real Runner GLB skin) with its own baked-in emissive
  // doesn't get silently overwritten by the flash the way an unconditional
  // `emissive.setScalar()` would (same gotcha the enemy version avoids).
  private readonly baseEmissive: THREE.Color;

  constructor(private readonly physics: BlockPhysics) {
    const cylinderLength = PLAYER_TOTAL_HEIGHT - PLAYER_RADIUS * 2;
    const geo = new THREE.CapsuleGeometry(PLAYER_RADIUS, cylinderLength, 4, 8);
    const mat = new THREE.MeshStandardMaterial({ color: 0xff7a2f, roughness: 0.75, metalness: 0 });
    this.mesh = new THREE.Mesh(geo, mat);
    this.baseEmissive = mat.emissive.clone();

    // Blob/contact shadow, not a realtime shadow map — §2's art language for phone tier.
    const shadowGeo = new THREE.PlaneGeometry(PLAYER_RADIUS * 2.6, PLAYER_RADIUS * 2.6);
    const shadowMat = new THREE.MeshBasicMaterial({
      map: createBlobShadowTexture(),
      transparent: true,
      depthWrite: false,
    });
    this.shadowBlob = new THREE.Mesh(shadowGeo, shadowMat);
    this.shadowBlob.rotation.x = -Math.PI / 2;

    this.syncMeshTransform();
  }

  addTo(scene: THREE.Scene): void {
    scene.add(this.mesh, this.shadowBlob);
  }

  teleport(x: number, z: number): void {
    this.position.x = x;
    this.position.z = z;
    const ground = this.physics.groundHeightAt(x, z, 50, 200); // generous tolerance: land on whatever's below, any level
    this.position.y = ground ?? 0;
    this.velocityY = 0;
    this.state.grounded = true;
    this.state.sliding = false;
    // T3-1 follow-up review fix (HIGH, correctness lens): killing a slide
    // here without also clearing its i-frames left `state.invulnerable`
    // latched TRUE forever — the only code that ever clears it is the
    // slide-expiry branch in update(), unreachable once `sliding` is false.
    // Latent since S02 for any teleport-during-slide (a USE tap inside the
    // 0.35s slide window), but the walk-out auto-exit (main.ts) is the
    // first teleport that fires unconditionally at a moment players
    // naturally slide, so "dash out the bodega door" would have granted
    // permanent, invisible immunity to every damage path.
    this.state.invulnerable = false;
    this.state.vaulting = false;
    this.slideTimer = 0;
    this.vaultTimer = 0;
    // T3-1 follow-up 2 verification finding (LOW): intent is a transient
    // like every other field this method resets — without this, a respawn
    // tick (update() skipped while dead) evaluates the walk-in gate with
    // the DEATH tick's held direction against the fresh respawn position.
    // Unexploitable today (no respawn point sits inside the door box), but
    // one future respawn-point move away from a false auto-enter.
    this.lastDesiredDirX = 0;
    this.lastDesiredDirZ = 0;
    // Review fix (LOW, dont-break lens): commandedSpeed is the same class of
    // per-tick transient every other field on this line resets — without
    // this it held whatever speed was commanded on the tick before the
    // teleport. Unobservable today (every teleport() call site either runs
    // after that tick's playerVisual.update() already read the old value,
    // or the very next update() call recomputes it in lockstep with
    // lastDesiredDirX/Z before anything reads it again), but the same
    // "reset every transient, don't rely on accidental protection from a
    // different field's gating" discipline this method already follows.
    this.commandedSpeed = 0;
    // A jump/coyote buffer left over from BEFORE the teleport (e.g. a press
    // that arrived while the caller had update() paused — S02's death/
    // respawn window is exactly this) must not survive into the teleported
    // state and fire an unrequested auto-jump the instant update() resumes.
    this.jumpBufferTimer = 0;
    this.coyoteTimer = 0;
    this.syncMeshTransform();
  }

  /** Edge-triggered: call once per jump-button press, not held every frame. */
  requestJump(): void {
    this.jumpBufferTimer = JUMP_BUFFER_TIME;
  }

  /** Mirrors ai/enemy.ts's flash-tint technique — `ratio` is `playerHealth.flashT / FLASH_DURATION`, computed by the caller (main.ts owns the S02 Health object, not this controller). */
  applyHitFlash(ratio: number): void {
    const mat = this.mesh.material as THREE.MeshStandardMaterial;
    mat.emissive.copy(this.baseEmissive).lerp(WHITE, ratio);
  }

  /** Current facing yaw (radians) — S02's aim-assist 'forward' preset and knockback direction both read this. */
  getFacing(): number {
    return this.facing;
  }

  /** R-4: this tick's true commanded speed (m/s) — see the field's own comment. */
  getCommandedSpeed(): number {
    return this.commandedSpeed;
  }

  /**
   * Nudges position by an externally-driven delta (S02's knockback) through
   * the same wall-resolution path ordinary movement uses, so a knockback
   * can't shove the player through a building. Does not touch velocityY,
   * grounded state, or any of the jump/vault/slide machinery — purely a
   * horizontal position adjustment layered on top of whatever `update()`
   * already decided this tick.
   *
   * No-ops while vaulting: `updateVault()` sets position.x/y/z ABSOLUTELY
   * every tick from fixed pre-vault-start endpoints (`lerpVectors`), which
   * would silently discard any nudge applied here a frame later anyway —
   * confirmed by a 3-lens review. Simplest correct behavior for this
   * ~0.28s window is to just not spend the knockback rather than compute a
   * push that never visibly lands.
   */
  applyExternalDelta(dx: number, dz: number): void {
    if (dx === 0 && dz === 0) return;
    if (this.state.vaulting) return;
    const resolved = this.physics.resolveHorizontal(this.position.x, this.position.z, dx, dz, PLAYER_RADIUS, this.position.y, this.position.y + PLAYER_TOTAL_HEIGHT);
    this.position.x = resolved.x;
    this.position.z = resolved.z;
    this.syncMeshTransform();
  }

  /** Edge-triggered: call once per slide gesture (Classic's swipe-down convention). */
  requestSlide(): void {
    if (this.state.sliding || this.state.vaulting || !this.state.grounded) return;
    this.state.sliding = true;
    this.state.invulnerable = true;
    this.slideTimer = SLIDE_DURATION;
    const len = Math.hypot(this.slideDirX, this.slideDirZ);
    if (len < 0.01) {
      // No live facing direction (player stood still and slid) — slide forward along current facing.
      this.slideDirX = Math.sin(this.facing);
      this.slideDirZ = Math.cos(this.facing);
    }
  }

  /**
   * `forward`/`right` are the orbit camera's flattened, normalized XZ basis
   * vectors — movement is camera-relative third-person, not tank controls.
   */
  update(dt: number, move: MoveInput, forward: THREE.Vector3, right: THREE.Vector3): void {
    // Captured before ANY of this tick's jump/vault/coyote bookkeeping
    // touches `state.grounded` — "grounded at the START of the tick" per
    // T1 3c's stick-to-ground rule below, which needs this to still read
    // true on a jump-launch tick (jump flips `state.grounded` to false
    // itself, a few lines down) so the velocityY<=0 check further down is
    // what actually excludes that frame, not this capture.
    const wasGrounded = this.state.grounded;
    if (this.jumpBufferTimer > 0) this.jumpBufferTimer -= dt;
    if (this.state.grounded) this.coyoteTimer = COYOTE_TIME;
    else if (this.coyoteTimer > 0) this.coyoteTimer -= dt;

    if (this.state.vaulting) {
      // No player intent while a vault auto-drives position — cleared so a
      // stale pre-vault direction can't satisfy an intent-gated trigger
      // (the bodega walk-in, main.ts) mid-vault. Review fix (MEDIUM,
      // spec-fidelity): commandedSpeed is a per-tick transient value same as
      // these two — its own assignment sits further down this method, past
      // this branch's early return, so without zeroing it here it stayed
      // frozen at its pre-vault value for the whole ~0.28s vault window.
      // Currently unobservable (playerVisual.ts never reads state.speed
      // while next==='jump', which state.vaulting forces) but a genuine
      // staleness bug for any future consumer — matches this field's own
      // "the true commanded speed THIS TICK" doc comment for real now.
      this.lastDesiredDirX = 0;
      this.lastDesiredDirZ = 0;
      this.commandedSpeed = 0;
      this.updateVault(dt);
      this.syncMeshTransform();
      return;
    }

    let dirX: number;
    let dirZ: number;
    let speed: number;
    if (this.state.sliding) {
      dirX = this.slideDirX;
      dirZ = this.slideDirZ;
      speed = SLIDE_SPEED;
      this.slideTimer -= dt;
      if (this.slideTimer <= 0) {
        this.state.sliding = false;
        this.state.invulnerable = false;
      }
    } else {
      const rawX = right.x * move.x + forward.x * -move.y;
      const rawZ = right.z * move.x + forward.z * -move.y;
      const rawLen = Math.hypot(rawX, rawZ);
      // Clamp to analog magnitude ≤1 (a diagonal stick input can exceed 1
      // before this) without discarding a genuinely slower partial push.
      const clamp = rawLen > 1 ? rawLen : 1;
      dirX = rawX / clamp;
      dirZ = rawZ / clamp;
      speed = MOVE_SPEED;
      if (rawLen > 0.01) {
        // Slide always launches at full speed in the last-held FACING
        // direction, so capture it normalized regardless of stick magnitude.
        this.slideDirX = rawX / rawLen;
        this.slideDirZ = rawZ / rawLen;
      }
    }

    // T3-1 follow-up 2: the player's DESIRED world-space movement direction
    // this tick (stick/keys through the camera basis, or the slide's own
    // committed direction) — captured before collision resolution, so an
    // intent-gated trigger (the bodega walk-in, main.ts) still reads "the
    // player is pushing north at the door" even when the door collider
    // clamps the achieved motion to zero. Plain number fields, no
    // allocation (S08's pooling discipline).
    this.lastDesiredDirX = dirX;
    this.lastDesiredDirZ = dirZ;
    // R-4: commanded speed = the (possibly partial-stick, magnitude <=1)
    // direction's length times whichever speed constant this tick used
    // (MOVE_SPEED or SLIDE_SPEED) — intent, not achieved-post-collision
    // motion, matching "commanded" rather than "actual displacement."
    this.commandedSpeed = Math.hypot(dirX, dirZ) * speed;

    const dx = dirX * speed * dt;
    const dz = dirZ * speed * dt;

    // Vault is checked BEFORE jump consumes the buffer — both read
    // `this.state.grounded`, and jump's own block below clears it. An
    // earlier version ran jump first, so a buffered jump press on the same
    // frame findVaultLedge would've returned a ledge silently pre-empted
    // the scripted auto-vault (the player still cleared the obstacle via
    // the jump arc in practice, but never got the vault the spec calls
    // for) — confirmed by a 3-lens review. Vaulting also returns early, so
    // a buffered jump on a vault frame is simply left buffered/decaying
    // rather than fired mid-vault, which is the same "consumed later or
    // expires" behavior any other action already gets when it can't fire
    // the instant it's requested.
    if (this.state.grounded && !this.state.sliding && (Math.abs(dx) > 1e-5 || Math.abs(dz) > 1e-5)) {
      const moveLen = Math.hypot(dx, dz);
      const ndx = dx / moveLen;
      const ndz = dz / moveLen;
      const ledge = this.physics.findVaultLedge(
        this.position.x,
        this.position.z,
        ndx,
        ndz,
        VAULT_PROBE_DIST,
        PLAYER_RADIUS,
        this.position.y,
        STEP_TOLERANCE,
        VAULT_MAX_HEIGHT,
        PLAYER_TOTAL_HEIGHT + 0.2,
      );
      if (ledge !== null) {
        this.startVault(ledge, ndx, ndz);
        this.syncMeshTransform();
        return;
      }
    }

    // Jump: consume the buffer the instant grounded-or-coyote is true, before
    // this frame's horizontal/vertical resolution runs. Gated on `!sliding`
    // too — without it, a buffered jump mid-slide left the player airborne
    // while the sliding branch above (which only checks `state.sliding`,
    // not `grounded`) kept driving them at SLIDE_SPEED along the frozen
    // pre-jump direction, unsteerable, until the slide timer ran out
    // (confirmed by a 3-lens review). `requestSlide()` already refuses to
    // start a NEW slide off the ground; this closes the other direction —
    // jump refusing to fire out of an ACTIVE one.
    if (this.jumpBufferTimer > 0 && !this.state.sliding && (this.state.grounded || this.coyoteTimer > 0)) {
      this.velocityY = JUMP_SPEED;
      this.state.grounded = false;
      this.coyoteTimer = 0;
      this.jumpBufferTimer = 0;
    }

    const resolved = this.physics.resolveHorizontal(
      this.position.x,
      this.position.z,
      dx,
      dz,
      PLAYER_RADIUS,
      this.position.y,
      this.position.y + PLAYER_TOTAL_HEIGHT,
    );
    this.position.x = resolved.x;
    this.position.z = resolved.z;

    const prevY = this.position.y;
    this.velocityY += GRAVITY * dt;
    let newY = this.position.y + this.velocityY * dt;
    const ground = this.physics.groundHeightAt(this.position.x, this.position.z, prevY, STEP_TOLERANCE);
    if (ground !== null) {
      // T1 3c: walking down a slope with only `newY <= ground` below reads
      // as a chain of tiny falls — velocityY resets to ~0 on every landing,
      // so gravity needs several ticks to accelerate the player downward as
      // fast as a 45° ramp's own descent rate; the ramp wins that race
      // every tick, `newY` (barely fallen) stays ABOVE `ground` (dropped
      // further), and `state.grounded` flips false for a frame before
      // flipping true again once the accumulated fall catches up — a
      // visible stutter, reproduced walking down the tunnel ramp (the exact
      // spot Karan circled reads as "falling through the floor" partly
      // because of this). Stick to the ground instead whenever the player
      // was already standing, isn't in the middle of a jump's launch frame
      // (gated on velocityY, not just wasGrounded — see wasGrounded's own
      // comment above for why wasGrounded alone isn't enough), and the drop
      // is small enough to be "still on the slope" rather than a real fall
      // (STEP_DOWN_TOLERANCE) — a genuine drop (roof edge, pit rim) always
      // exceeds it and still free-falls exactly as before.
      const stepDown = wasGrounded && this.velocityY <= 0 && ground <= prevY && prevY - ground <= STEP_DOWN_TOLERANCE;
      if (stepDown || newY <= ground) {
        newY = ground;
        this.velocityY = 0;
        this.state.grounded = true;
      } else {
        this.state.grounded = false;
      }
    } else {
      this.state.grounded = false;
    }
    this.position.y = newY;

    // Safety net: falling off the map entirely (e.g. off the rooftop away
    // from any floor region) — recover instead of falling forever.
    if (this.position.y < -20) {
      this.teleport(0, -6);
    }

    if (Math.hypot(dirX, dirZ) > 0.01) {
      this.facing = shortestAngleLerp(this.facing, Math.atan2(dirX, dirZ), Math.min(1, TURN_RATE * dt));
    }

    this.syncMeshTransform();
  }

  private startVault(ledge: { y: number; minX: number; maxX: number; minZ: number; maxZ: number }, dirX: number, dirZ: number): void {
    this.state.vaulting = true;
    this.vaultTimer = VAULT_DURATION;
    this.vaultFrom.set(this.position.x, this.position.y, this.position.z);
    const travel = VAULT_PROBE_DIST + 0.3;
    // Clamp the landing XZ to stay INSIDE the ledge's own footprint (inset
    // by the capsule radius so the player doesn't land hanging half off the
    // edge) — a fixed travel distance triggered near-range on a shallow
    // prop (this block's 0.6m-deep benches) could otherwise overshoot past
    // its far edge onto empty air, landing at the right height but on no
    // floor region, so the very next tick free-falls the remaining drop —
    // a visible stutter, confirmed by a 3-lens review.
    const landX = THREE.MathUtils.clamp(this.position.x + dirX * travel, ledge.minX + PLAYER_RADIUS, ledge.maxX - PLAYER_RADIUS);
    const landZ = THREE.MathUtils.clamp(this.position.z + dirZ * travel, ledge.minZ + PLAYER_RADIUS, ledge.maxZ - PLAYER_RADIUS);
    this.vaultTo.set(landX, ledge.y, landZ);
  }

  private updateVault(dt: number): void {
    this.vaultTimer -= dt;
    const t = THREE.MathUtils.clamp(1 - Math.max(this.vaultTimer, 0) / VAULT_DURATION, 0, 1);
    const eased = t * t * (3 - 2 * t); // smoothstep
    this.position.lerpVectors(this.vaultFrom, this.vaultTo, eased);
    if (this.vaultTimer <= 0) {
      this.position.copy(this.vaultTo);
      this.state.vaulting = false;
      this.state.grounded = true;
      this.velocityY = 0;
    }
  }

  private syncMeshTransform(): void {
    this.mesh.position.set(this.position.x, this.position.y + PLAYER_TOTAL_HEIGHT / 2, this.position.z);
    this.mesh.rotation.y = this.facing;
    this.shadowBlob.position.set(this.position.x, this.position.y + 0.01, this.position.z);
  }
}

function createBlobShadowTexture(): THREE.CanvasTexture {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  gradient.addColorStop(0, 'rgba(0,0,0,0.45)');
  gradient.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  return new THREE.CanvasTexture(canvas);
}
